import React, { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceService, {
    extractErrorMessage,
    SellerBillingProfile,
    SellerReadiness,
} from '../../../services/marketplaceService';
import { IconBadge } from './SellerBadges';
import MaterialSymbol from '../../common/MaterialSymbol';
import '../marketplace-design.css';

/** Eine Zeile der Stammdaten; fehlende Werte stehen rot mit „fehlt“ (Vorlage `dd.miss`). */
const Row: React.FC<{ label: string; value: React.ReactNode; required?: boolean; missing: string }> = ({ label, value, required = false, missing }) => (
    <>
        <dt>{label}</dt>
        {value ? (
            <dd>{value}</dd>
        ) : required ? (
            <dd className="miss"><MaterialSymbol name="error" />{missing}</dd>
        ) : (
            <dd>–</dd>
        )}
    </>
);

/**
 * PayPal-Verbindung + Rechnungsstammdaten (lesend, Plan 3.7). Das Onboarding
 * ist bis T2 ein Stub: der Button ruft den Endpunkt auf und zeigt dessen
 * Hinweis, sobald das Backend (Partner-Referral) `actionUrl` liefert,
 * leitet er dorthin weiter.
 */
const SellerBillingPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [profile, setProfile] = useState<SellerBillingProfile | null>(null);
    const [readiness, setReadiness] = useState<SellerReadiness | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [connecting, setConnecting] = useState(false);

    const load = useCallback(async (refresh = false) => {
        setError(null);
        try {
            const result = await marketplaceService.getSellerBillingProfile(refresh);
            setProfile(result.profile);
            setReadiness(result.readiness);
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceSeller.billing.loadError')));
        }
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sync = async () => {
        setSyncing(true);
        await load(true);
        setSyncing(false);
        notify(translate('marketplaceSeller.billing.synced'), { type: 'info' });
    };

    const connect = async () => {
        setConnecting(true);
        try {
            const result = await marketplaceService.startPaypalOnboarding();
            if (result.actionUrl) {
                window.location.assign(result.actionUrl);
            }
        } catch (err: any) {
            const status = err?.response?.status;
            notify(
                status === 501 ? translate('marketplaceSeller.billing.paypal.notAvailable') : extractErrorMessage(err, translate('marketplaceSeller.billing.paypal.error')),
                { type: status === 501 ? 'info' : 'error' },
            );
        } finally {
            setConnecting(false);
        }
    };

    if (error) {
        return (
            <div className="mk-alert err" role="alert">
                <MaterialSymbol name="error" />
                <div className="body">{error}</div>
            </div>
        );
    }
    if (readiness === null) return null;

    const status = readiness.paypalStatus;
    const connected = readiness.paypalReady;
    const address = profile ? [profile.street, [profile.zip, profile.city].filter(Boolean).join(' '), profile.country].filter(Boolean).join(', ') : '';
    const missing = translate('marketplaceSeller.billing.profile.missing');

    return (
        <div className="mk-two">
            <div className="mk-card" data-testid="paypal-status-card">
                <div className="ch">
                    <IconBadge name="payment" size={42} />
                    <h3>{translate('marketplaceSeller.billing.paypal.title')}</h3>
                    <span className="sp" />
                    <span className={`mk-pill ${connected ? 'ok' : status === 'pending' ? 'warn' : status === 'revoked' ? 'err' : 'neu'}`} data-testid="paypal-status">
                        <MaterialSymbol name={connected ? 'check_circle' : status === 'pending' ? 'hourglass_top' : 'info'} />
                        {translate(`marketplaceSeller.billing.paypal.status.${status}`)}
                    </span>
                </div>
                <p className="mk-pp-text">
                    {translate(connected ? 'marketplaceSeller.billing.paypal.connectedText' : 'marketplaceSeller.billing.paypal.explain')}
                </p>
                <p className="foot-note" style={{ marginTop: 8 }}>{translate('marketplaceSeller.billing.paypal.groupHint')}</p>
                {!connected && (
                    <div style={{ marginTop: 18 }}>
                        <button type="button" className="mk-btn-s" onClick={connect} disabled={connecting}>
                            {translate('marketplaceSeller.billing.paypal.connect')}
                        </button>
                    </div>
                )}
            </div>

            <div className="mk-card" data-testid="billing-profile-card">
                <div className="ch">
                    <IconBadge name="receipt_long" size={42} />
                    <h3>{translate('marketplaceSeller.billing.profile.title')}</h3>
                    <span className="sp" />
                    <button type="button" className="mk-lnk" onClick={sync} disabled={syncing}>
                        <MaterialSymbol name="sync" />
                        {translate('marketplaceSeller.billing.profile.sync')}
                    </button>
                </div>
                {!profile || !readiness.billingComplete ? (
                    <div className="mk-alert warn" style={{ marginBottom: 18 }} role="note">
                        <MaterialSymbol name="warning" />
                        <div className="body"><p style={{ color: 'var(--oa-text)' }}>{translate('marketplaceSeller.billing.profile.incomplete')}</p></div>
                    </div>
                ) : null}
                {profile ? (
                    <dl className="mk-dl">
                        <Row label={translate('marketplaceSeller.billing.profile.legalName')} value={profile.legalName} missing={missing} />
                        <Row label={translate('marketplaceSeller.billing.profile.address')} value={address} missing={missing} />
                        <Row label={translate('marketplaceSeller.billing.profile.taxNumber')} value={profile.taxNumber} required missing={missing} />
                        <Row label={translate('marketplaceSeller.billing.profile.vatId')} value={profile.vatId} required missing={missing} />
                        <Row label={translate('marketplaceSeller.billing.profile.smallBusiness')} value={translate(profile.smallBusiness ? 'ra.boolean.true' : 'ra.boolean.false')} missing={missing} />
                        <Row label={translate('marketplaceSeller.billing.profile.email')} value={profile.billingEmail} missing={missing} />
                        <Row label={translate('marketplaceSeller.billing.profile.iban')} value={profile.iban} required missing={missing} />
                        <Row label={translate('marketplaceSeller.billing.profile.accountHolder')} value={profile.accountHolder} required missing={missing} />
                    </dl>
                ) : (
                    <p style={{ margin: 0, fontSize: '.9rem' }}>{translate('marketplaceSeller.billing.profile.none')}</p>
                )}
                <p className="foot-note">{translate('marketplaceSeller.billing.profile.editHint')}</p>
            </div>
        </div>
    );
};

export default SellerBillingPanel;
